import { z } from "zod";
import { Theme, UiLang } from "./identity";

/** A path inside the app. Never another origin: a parent cannot navigate the iframe away with it. */
export const AppPath = z.string().max(2000).regex(/^\/(?!\/)[^\\\s]*$/);

/** What the iframe tells its parent. Sent only to an origin from `allowedOrigins` of the exchange. */
export const EmbedToParent = z.discriminatedUnion("type", [
  z.object({ type: z.literal("imagora:ready"), contract: z.string(), path: AppPath }),
  /** The height the content wants, in CSS pixels. Hosts that size the iframe to its content use this. */
  z.object({ type: z.literal("imagora:resize"), height: z.number().int().nonnegative() }),
  /** The parent answers with `imagora:token`: a fresh SSO token, signed like the first one. */
  z.object({ type: z.literal("imagora:need-token"), reason: z.enum(["boot", "expiring", "expired"]) }),
  /** The user moved inside the app. */
  z.object({ type: z.literal("imagora:navigate"), path: AppPath }),
]);
export type EmbedToParent = z.infer<typeof EmbedToParent>;

/** What the parent tells the iframe. Accepted only from the parent window, at an allowed origin. */
export const EmbedFromParent = z.discriminatedUnion("type", [
  z.object({ type: z.literal("imagora:token"), token: z.string().min(1).max(16_384) }),
  z.object({ type: z.literal("imagora:set-theme"), theme: Theme }),
  z.object({ type: z.literal("imagora:set-lang"), lang: UiLang }),
  z.object({ type: z.literal("imagora:navigate"), path: AppPath }),
  /**
   * For a host that grows the iframe to its content and scrolls its own page: the part of the iframe on screen, in the
   * iframe's coordinates. Overlays and bars stay in that part. Hosts with a fixed-height iframe do not send it.
   */
  z.object({ type: z.literal("imagora:viewport"), top: z.number().nonnegative(), height: z.number().positive() }),
]);
export type EmbedFromParent = z.infer<typeof EmbedFromParent>;
